import type { Country } from "../types";

import { Button, Chip } from "@heroui/react";
import { Pencil, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import CountryFormModal from "@/components/country/country-form-modal";
import {
  DataTable,
  ImageTile,
  useClientTable,
  type DataGridColumn,
} from "@/components/kit";
import { useOpenOnAddParam } from "@/hooks/useOpenOnAddParam";
import { AppPage } from "@/layouts/page";
import { CountryRepository } from "@/repositories/country";

const countryRepository = new CountryRepository();

export default function CountriesTable() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  useOpenOnAddParam(() => setAddModalOpen(true));

  const load = useCallback(() => {
    setLoading(true);
    countryRepository
      .findAll()
      .then((data) => {
        setCountries(data);
      })
      .catch((error) => {
        console.error("Failed to fetch countries:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(load, [load]);

  const columns = useMemo<DataGridColumn<Country>[]>(
    () => [
      {
        id: "name",
        header: "Country",
        isRowHeader: true,
        allowsSorting: true,
        sortFn: (a, b) => a.name.localeCompare(b.name),
        cell: (country) => (
          <div className="flex items-center gap-3">
            <ImageTile size="sm" src={country.image} />
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-medium">{country.name}</span>
              <span
                className="text-muted truncate text-left text-xs"
                lang={country.nameAr ? "ar" : undefined}
              >
                {country.nameAr || "-"}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "isVisible",
        header: "Visibility",
        width: 160,
        allowsSorting: true,
        sortFn: (a, b) => Number(b.isVisible) - Number(a.isVisible),
        cell: (country) => (
          <Chip
            color={country.isVisible ? "success" : "default"}
            variant="soft"
          >
            {country.isVisible ? "Visible" : "Hidden"}
          </Chip>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        width: 64,
        align: "end",
        cell: (country) => (
          <Button
            isIconOnly
            aria-label={`Edit ${country.name}`}
            size="sm"
            variant="ghost"
            onPress={() => setSelectedCountry(country)}
          >
            <Pencil className="size-4" />
          </Button>
        ),
      },
    ],
    [],
  );

  const table = useClientTable(countries, {
    columns,
    initialSort: { column: "name", direction: "ascending" },
    pageSize: 50,
  });

  return (
    <AppPage
      action={
        <Button onPress={() => setAddModalOpen(true)}>
          <Plus className="size-4" />
          Add country
        </Button>
      }
      subtitle={
        <>
          <span className="tabular-nums">{countries.length}</span>{" "}
          {countries.length === 1 ? "country" : "countries"} in the catalog
        </>
      }
      title="Countries"
    >
      <div className="flex min-h-0 max-w-3xl flex-1 flex-col">
        <DataTable
          compact
          columns={columns}
          empty={{
            title: "No countries found",
            description: "Get started by adding your first country.",
          }}
          getRowId={(country) => country.id}
          isLoading={loading}
          label="Countries"
          rows={table.rows}
          sort={table.sort}
          onSortChange={table.onSortChange}
        />
      </div>

      <CountryFormModal
        country={selectedCountry}
        isOpen={!!selectedCountry}
        onClose={() => setSelectedCountry(null)}
        onSuccess={load}
      />

      <CountryFormModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={load}
      />
    </AppPage>
  );
}
